import type { TimelineEvent, TimelineFilterState } from '@/types/timeline'
import { Grid } from '@mui/material'
import { endOfDay, isAfter, isBefore, startOfDay } from 'date-fns'
import { useMemo, useState } from 'react'
import TimelineFilters from './TimelineFilters'
import TimelineList from './TimelineList'
import type { TimelineCategory } from './config'

const DEFAULT_ITEMS_PER_PAGE = 12

interface TimelineViewProps {
  entityName: string
  events: TimelineEvent[]
  totalItems?: number
  itemsPerPage?: number
  patientHash?: string
  excludedFilters?: TimelineCategory[]
}

function applyFilters(events: TimelineEvent[], filters: TimelineFilterState): TimelineEvent[] {
  return events.filter(event => {
    if (filters.search) {
      const q = filters.search.toLowerCase()
      const match =
        event.title.toLowerCase().includes(q) ||
        event.description.toLowerCase().includes(q) ||
        event.user.toLowerCase().includes(q)
      if (!match) return false
    }
    if (filters.categories.length > 0 && !filters.categories.includes(event.category)) return false
    if (filters.sources.length > 0 && !filters.sources.includes(event.source)) return false
    if (filters.dateFrom && isBefore(event.timestamp, startOfDay(filters.dateFrom))) return false
    if (filters.dateTo && isAfter(event.timestamp, endOfDay(filters.dateTo))) return false
    return true
  })
}

export default function TimelineView({
  entityName,
  events,
  totalItems,
  itemsPerPage = DEFAULT_ITEMS_PER_PAGE,
  patientHash,
  excludedFilters = []
}: TimelineViewProps) {
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<TimelineFilterState>({
    search: '',
    categories: [],
    sources: [],
    dateFrom: null,
    dateTo: null
  })

  const filteredEvents = useMemo(() => applyFilters(events, filters), [events, filters])

  const sortedEvents = useMemo(
    () => [...filteredEvents].sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()),
    [filteredEvents]
  )

  const effectiveTotalItems = totalItems ?? sortedEvents.length
  const totalPages = Math.max(1, Math.ceil(sortedEvents.length / itemsPerPage))
  const paginatedEvents = useMemo(() => {
    const start = (page - 1) * itemsPerPage
    return sortedEvents.slice(start, start + itemsPerPage)
  }, [sortedEvents, page, itemsPerPage])

  const handleFiltersChange = (next: TimelineFilterState) => {
    setFilters(next)
    setPage(1)
  }

  return (
    <Grid container spacing={3}>
      <Grid size={8}>
        <TimelineList
          entityName={entityName}
          events={paginatedEvents}
          totalItems={effectiveTotalItems}
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          patientHash={patientHash}
        />
      </Grid>
      <Grid size={4}>
        <TimelineFilters filters={filters} onFiltersChange={handleFiltersChange} excludedFilters={excludedFilters} />
      </Grid>
    </Grid>
  )
}
